import { useQuery } from '@tanstack/react-query';
import { getUpcomingLiveQuizzes } from '../services/liveQuizService';
import { LiveEventSummary, LiveRoomStatus } from '../types/liveQuiz';
import { anchor, serverNow } from '../utils/serverClock';

/** Query key shared with the quizzes-tab live badge, so both surfaces read one cache. */
export const UPCOMING_LIVE_QUIZZES_QUERY_KEY = ['live', 'upcoming'] as const;

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const FAR_POLL_MS = 60_000;
const NEAR_POLL_MS = 15_000;

function isLiveNow(status: LiveRoomStatus | null): boolean {
  return status === 'question' || status === 'reveal' || status === 'scoreboard';
}

/**
 * Refetch cadence, derived from how urgent the nearest event is: off when
 * nothing is within two hours, 60s once something is, tightening to 15s once
 * anything has reached its lobby. A slow poll still *feels* instant because
 * the consumer also fires an immediate refetch via `useServerCountdown`'s
 * `onExpire` the moment a deadline crosses — this cadence only covers
 * everything in between.
 */
export function refetchIntervalFor(events: LiveEventSummary[]): number | false {
  let nearestLobbyGapMs = Infinity;

  for (const event of events) {
    if (event.status === 'lobby' || isLiveNow(event.status)) {
      return NEAR_POLL_MS;
    }
    if (event.status === 'finished' || event.status === 'abandoned') {
      continue;
    }
    nearestLobbyGapMs = Math.min(nearestLobbyGapMs, Date.parse(event.lobbyOpensAt) - serverNow());
  }

  return nearestLobbyGapMs <= TWO_HOURS_MS ? FAR_POLL_MS : false;
}

/**
 * Upcoming/live events visible to the current student, for the dashboard
 * banner and the quizzes-page live badge. Every fetch re-anchors the shared
 * server clock from the response's own timestamp, the same discipline
 * `useLiveQuizRoom` uses for its payloads.
 */
export function useUpcomingLiveQuizzes() {
  return useQuery({
    queryKey: UPCOMING_LIVE_QUIZZES_QUERY_KEY,
    queryFn: async () => {
      const data = await getUpcomingLiveQuizzes();
      anchor(data.serverTime);
      return data;
    },
    refetchInterval: query => refetchIntervalFor(query.state.data?.events ?? [])
  });
}
